import { Alert, Box, Checkbox, FormGroup, Loader } from '@ksyos/design-system';
import { useEffect, useRef, useState } from 'react';
import type { OptionalFollowUpConsultCondition } from '@common/customControls/tooV1Diagnose';
import type { ConclusieDiagnoseOption } from '@common/enums';
import type { PatientInsuranceHistory } from '@common/interfaces/api/patientsApi';
import type { ITooDiagnosticReportCustomControlData } from '@common/interfaces/ophthalmology/TOO/tooDiagnosticReportCustomControlData';
import type {
    IOphthalmologyAsset,
    ITooDiagnosticReportCustomControlDataOptometrischeDiagnose,
    ITooProcedureCustomControlData,
} from '@common/validation/customControls/ophthalmology';
import type { IServiceConsumer } from '@frontend/components/interfaces';
import type { ValidationErrors } from '@frontend/utils';
import { ProcessEntity, saveTypeEnum, ServiceCode, ToastType } from '@common/enums';
import { OphthalmologyFilePlacement, TooProtocol } from '@common/validation/customControls/ophthalmology';
import {
    checkIsReferralCheckRequired,
    requestServiceConditionals,
} from '@frontend/components/pages/customControls/TOO/v1/serviceConditionalsService';
import {
    addMinusCylindrishValues,
    removeMinusCylindrishValues,
    TooV1ProcedureAddEdit,
} from '@frontend/components/pages/customControls/TOO/v1/TooV1ProcedureAddEdit';
import { NewOrEditDiagnosticReportProcessContainer } from '@frontend/components/pages/customControls/utils';
import { ReloadEpisode } from '@frontend/components/pages/episodes/reloadEpisode';
import { getActiveInsuranceFromInsuranceHistory } from '@frontend/services/api/patientInsuranceApiService';
import { fetchJson } from '@frontend/store/fetch';
import { translateKey } from '@frontend/translate';
import { emptyValidationErrors, renameErrorKeys, showServiceToast } from '@frontend/utils';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import type { IService } from '../../../../interfaces/service';
import type { IEvaluation } from '../../../evaluation/EvaluationRequestItem';
import type { IProcedureAddEdit } from '../../../procedures/ProcedureItemAddEdit';
import type { ICustomControlProcedureDiagnosticReportAddEditProps } from '../../interfaces/';
import { Form } from '../../../../elements/common';
import { ProceedWithoutFiles } from '../../../../elements/ProceedWithoutFiles';
import { EvaluationRequestItem } from '../../../evaluation/EvaluationRequestItem';
import { addFormAnalytics, addFormConversion, addTrackFormSubmit } from '../../utils/formAnalytics';
import { TooV1CustomControlFields } from './TooV1DiagnosticReportAddEdit';
import { TooV1DiagnosticReportAddEditCustomControlFields } from './TooV1DiagnosticReportAddEditCustomControlFields';
import { TooV1ProcedureDiagnosticReportAddEditFooterButtons } from './TooV1ProcedureDiagnosticReportAddEditFooterButtons';
import { TooV1TocProcedureDiagnosticReportAddEdit } from './TooV1TocProcedureDiagnosticReportAddEdit';
import { validateReferralCheck } from './validateReferralCheck';

export type TooV1ProcedureRequiredFields = {
    amsler_test: boolean;
    ervaart_klachten_als_storend: boolean;
    gonioscopie: boolean;
    nabije_visus: boolean;
    pachymetrie: boolean;
    rijdt_auto: boolean;
};

const tooV1RequiredFields = [
    'pachymetrie',
    'gonioscopie',
    'amsler_test',
    'nabije_visus',
    'rijdt_auto',
    'ervaart_klachten_als_storend',
];

export function TooV1ProcedureDiagnosticReportAddEdit(props: ICustomControlProcedureDiagnosticReportAddEditProps) {
    const {
        diagnosticReport,
        handleChange,
        handleChangeCustomControl,
        handleChangeCustomControlDiagnosticReport,
        handleChangeDiagnosticReport,
        procedure,
        request,
    } = props;

    const { conclusie_diagnose, optometrische_diagnose } =
        diagnosticReport.customControlData as ITooDiagnosticReportCustomControlData;
    const other_diagnosis = Object.entries(diagnosticReport.diagnosis).some(
        ([key, value]) => key !== 'diagnosisConsiderationIcd10fallback' && value
    );

    const abortSignal = useAbortSignal();
    const formRef = useRef<HTMLFormElement>(null);
    const saveSendButtonRef = useRef<HTMLButtonElement>(null);
    const [activeInsurance, setActiveInsurance] = useState<PatientInsuranceHistory | null>(null);
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [isInitializing, setIsInitializing] = useState(true);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isTocConsumer, setIsTocConsumer] = useState(true);
    const [optionalFollowUpConsultConditions, setOptionalFollowUpConsultConditions] = useState<
        OptionalFollowUpConsultCondition[]
    >([]);
    const [showOptionalConsultationCheckbox, setShowOptionalConsultationCheckbox] = useState<boolean>(false);
    const [showConsultation, setShowConsultation] = useState<boolean>(
        Boolean(procedure.customControlData.consult) || !showOptionalConsultationCheckbox
    );
    const [showEvaluation, setShowEvaluation] = useState(false);
    const [showProceedWithoutFiles, setShowProceedWithoutFiles] = useState(false);
    const [tocService, setTocService] = useState<IService | undefined>();
    const [tooRequiredFields, setTooRequiredFields] = useState<Partial<TooV1ProcedureRequiredFields>>({});
    const [referralCheckErrors, setReferralCheckErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [isReferralCheckCompleted, setIsReferralCheckCompleted] = useState(false);
    const [hasCareGroupTOOFinancingAgreement, setHasCareGroupTOOFinancingAgreement] = useState<boolean>(false);

    const isReferralCheckRequired = checkIsReferralCheckRequired({
        activeInsurance: activeInsurance ?? null,
        isDirectProcedureAdded: Boolean(request.isDirectProcedureAdded),
    });

    // Get relevant data for the form
    useEffect(() => {
        initializeComponent({
            employmentId: props.employmentId,
            patientId: request.patientId,
        });
    }, [props.employmentId, request.patientId]);

    // Determine the conditions for when a consultation is required
    useEffect(() => {
        loadOptionalFollowUpConsultConditions(
            request.patientId,
            procedure.draftCareProviderOrganizationId,
            procedure.draftCareProviderPractitionerId
        );
    }, [request.patientId, procedure.draftCareProviderOrganizationId, procedure.draftCareProviderPractitionerId]);

    const showGlaucoomAlertMessage =
        procedure.customControlData.pachymetrie_od === null ||
        procedure.customControlData.pachymetrie_os === null ||
        procedure.customControlData.gonioscopie_od === null ||
        procedure.customControlData.gonioscopie_os === null;

    // Determine if showConsultation checkbox should be shown
    // And if the consultation itself should be shown
    useEffect(() => {
        const hasOptionalConsultation = hasOptionalConsultDueToDiagnosis({
            conclusie_diagnose,
            optionalFollowUpConsultConditions,
            optometrische_diagnose,
            other_diagnosis,
        });
        setShowOptionalConsultationCheckbox((prevValue) => {
            if (Boolean(prevValue) !== hasOptionalConsultation) {
                // Only if the value changes:
                setShowConsultation(!hasOptionalConsultation);
                // also, clear the consult value
                handleChangeCustomControl({ consult: undefined });
            }

            return hasOptionalConsultation;
        });
    }, [conclusie_diagnose, optometrische_diagnose, other_diagnosis, optionalFollowUpConsultConditions]);

    const footerButtons = (
        <TooV1ProcedureDiagnosticReportAddEditFooterButtons
            isDisabled={isButtonDisabled}
            isExisting={Boolean(props.request.id)}
            onCancel={async () => await props.onCancel()}
            onSave={async (status) => {
                await handleSaveProcedure(status);
            }}
            revertChanges={async () => {
                if (props.revertProcedureChanges) {
                    await props.revertProcedureChanges();
                }
            }}
            saveSendButtonRef={saveSendButtonRef}
            service={props.service}
        />
    );

    if (isInitializing) {
        return (
            <Box
                alignItems="center"
                display="flex"
                justifyContent="center"
                paddingY={6}
            >
                <Loader />
            </Box>
        );
    }

    return (
        <Form
            formRef={formRef}
            id="TooV1ProcedureDiagnosticReportAddEditId"
            name="TooV1ProcedureDiagnosticReportAddEdit"
            onSubmit={async () => null}
        >
            <>
                <TooV1ProcedureAddEdit
                    {...props}
                    activeInsurance={activeInsurance}
                    handleChange={handleChange}
                    handleChangeDiagnosticReportAddEditCustomControlFields={
                        handleChangeDiagnosticReportAddEditCustomControlFields
                    }
                    handleSetHasCareGroupTOOFinancingAgreement={setHasCareGroupTOOFinancingAgreement}
                    handleSetIsReferralCheckCompleted={setIsReferralCheckCompleted}
                    handleSetReferralCheckErrors={setReferralCheckErrors}
                    isBioGrader
                    isSubmitted={isSubmitted}
                    referralCheckErrors={referralCheckErrors}
                    requiredFields={tooRequiredFields}
                    validateOneCustomControl={props.validateOneCustomControl}
                >
                    {!isTocConsumer ? (
                        <Alert appearance="warning">
                            {translateKey('diagnosticReport.noConsumerOfCoupledOphthalmologyConsult')}
                        </Alert>
                    ) : undefined}
                </TooV1ProcedureAddEdit>
                {isReferralCheckCompleted ? (
                    <>
                        <NewOrEditDiagnosticReportProcessContainer
                            currentDiagnosticReport={props.currentDiagnosticReport}
                            itemState={props.itemState}
                            process={props.process}
                            service={props.service}
                            title={translateKey('diagnosticReport.findings')}
                        >
                            <TooV1DiagnosticReportAddEditCustomControlFields
                                customControlData={props.diagnosticReport.customControlData}
                                diagnosticReport={props.diagnosticReport}
                                errors={renameErrorKeys(
                                    props.errors,
                                    'diagnosticReport.customControlData',
                                    'customControlData'
                                )}
                                handleChange={handleChangeDiagnosticReport}
                                handleChangeCustomControl={handleChangeDiagnosticReportAddEditCustomControlFields}
                                service={props.service}
                                showGlaucoomAlertMessage={showGlaucoomAlertMessage}
                                validateOne={props.validateOneCustomControlDiagnosticReport}
                                validateOneCustomControl={props.validateOneCustomControlDiagnosticReport}
                            />
                            {showOptionalConsultationCheckbox && (
                                <FormGroup label="Consult aanvragen">
                                    <Checkbox
                                        checked={showConsultation}
                                        onChange={(event) => {
                                            handleOnChangeConsultationCheckbox(event.target.checked);
                                        }}
                                    />
                                </FormGroup>
                            )}
                            {!showConsultation && isTocConsumer && footerButtons}
                        </NewOrEditDiagnosticReportProcessContainer>

                        {tocService && showConsultation && (
                            <TooV1TocProcedureDiagnosticReportAddEdit
                                tocService={tocService}
                                {...props}
                            >
                                {footerButtons}
                            </TooV1TocProcedureDiagnosticReportAddEdit>
                        )}
                        {showProceedWithoutFiles && (
                            <ProceedWithoutFiles
                                entity={ProcessEntity.procedure}
                                onNo={async () => {
                                    setIsButtonDisabled(false);
                                    setShowProceedWithoutFiles(false);
                                }}
                                onYes={async () => {
                                    setShowProceedWithoutFiles(false);
                                    await saveProcedure(saveTypeEnum.final, { proceedWithoutFiles: true });
                                }}
                                serviceType={props.service.serviceType}
                            />
                        )}
                        {showEvaluation && (
                            <EvaluationRequestItem
                                handleChange={async (name, value) => await handleEvaluationChange(name, value)}
                                onClose={async () => {
                                    setShowEvaluation(false);
                                    await saveProcedure(saveTypeEnum.final, {
                                        proceedWithoutEvaluation: true,
                                        proceedWithoutFiles: true,
                                    });
                                }}
                            />
                        )}
                    </>
                ) : (
                    <div className="form-container no-border-top">{footerButtons}</div>
                )}
            </>
        </Form>
    );

    async function initializeComponent({
        employmentId,
        patientId,
    }: {
        employmentId: string;
        patientId: string;
    }): Promise<void> {
        setIsInitializing(true);
        const isTocServiceConsumer = await isServiceConsumerOfToc(employmentId);
        const tocServiceLocal = await loadTocService(isTocServiceConsumer);
        setIsTocConsumer(isTocServiceConsumer);
        setTocService(tocServiceLocal);

        const activeInsuranceLocal = await getActiveInsuranceFromInsuranceHistory(patientId, abortSignal);
        setActiveInsurance(activeInsuranceLocal);

        // This makes no sense, function is only called at initial render and values are null then
        if (formRef.current && saveSendButtonRef.current) {
            addFormAnalytics(formRef.current, saveSendButtonRef.current);
        }
        setIsInitializing(false);
    }

    async function handleSaveProcedure(status: saveTypeEnum) {
        if (status === saveTypeEnum.draft) {
            return await saveProcedure(saveTypeEnum.draft);
        }

        const { hasErrors, errors: localErrors } = validateReferralCheck({
            hasCareGroupTOOFinancingAgreement,
            isDirectProcedureAdded: Boolean(request.isDirectProcedureAdded),
            isReferralCheckRequired,
            procedure,
        });
        setReferralCheckErrors(localErrors);

        if (!hasErrors && isReferralCheckCompleted) {
            setTooRequiredFields(checkRequiredFields(optometrische_diagnose));
            setIsSubmitted(true);
            await saveProcedure(saveTypeEnum.final);
            setIsSubmitted(false);
        }
    }

    async function handleChangeDiagnosticReportAddEditCustomControlFields(
        data: Partial<ITooDiagnosticReportCustomControlData>
    ) {
        await handleChangeCustomControlDiagnosticReport(data);
        setTooRequiredFields(checkRequiredFields(optometrische_diagnose));
    }

    async function handleOnChangeConsultationCheckbox(isChecked: boolean) {
        setShowConsultation(isChecked);
        if (!isChecked) {
            await handleChangeCustomControl({ consult: undefined });
        }
    }

    async function saveProcedure(
        saveType: saveTypeEnum,
        options?: { proceedWithoutEvaluation?: boolean; proceedWithoutFiles?: boolean }
    ) {
        const formNode = formRef.current;
        const buttonNode = saveSendButtonRef.current;

        setIsButtonDisabled(true);

        if (!options?.proceedWithoutFiles && !showEvaluation) {
            addTrackFormSubmit(buttonNode);
        }

        const updatedCylindrishValues = addMinusCylindrishValues(props.procedure.customControlData);
        await props.handleChangeCustomControl(updatedCylindrishValues);

        const validatedProcedure = await props.validateProcedure(saveType);
        if (!validatedProcedure) {
            const newCylindrishValues = removeMinusCylindrishValues(props.procedure.customControlData);
            await props.handleChangeCustomControl(newCylindrishValues);
            setIsButtonDisabled(false);

            return;
        }

        if (
            !options?.proceedWithoutFiles &&
            saveType === saveTypeEnum.final &&
            props.procedure.assets.length === 0 &&
            props.procedure.customControlData.files?.filter(
                (file: IOphthalmologyAsset) => file.placement !== OphthalmologyFilePlacement.verwijsbrief
            ).length === 0
        ) {
            setShowProceedWithoutFiles(true);

            return;
        }

        if (!options?.proceedWithoutEvaluation && saveType === saveTypeEnum.final && showConsultation) {
            setShowEvaluation(true);

            return;
        }

        const requestId = await props.onSaveProcedure(saveType);
        if (!requestId) {
            setIsButtonDisabled(false);

            return;
        }

        await props.onSave(requestId);

        const hasConsultation = props.procedure.customControlData?.consult;

        showServiceToast(
            ToastType.success,
            saveType === saveTypeEnum.draft ? 'toast.draftSuccess' : 'toast.submitSuccess',
            hasConsultation ? ServiceCode.TOC : ServiceCode.TOO,
            hasConsultation ? 'request' : 'procedure',
            translateKey('toast.draftDefaultMessage')
        );

        if (saveType === saveTypeEnum.final) {
            props.reloadProcess(ReloadEpisode.reloadAll);
            addFormConversion(formNode);
        }
    }

    async function handleEvaluationChange<K extends keyof IEvaluation>(name: K, value: IEvaluation[K]) {
        const consultData = props.procedure.customControlData.consult;
        consultData.evaluation = { ...consultData.evaluation, [name]: value };
        await props.handleChangeCustomControl({ consult: consultData });
    }

    async function loadOptionalFollowUpConsultConditions(
        patientId: string,
        careProviderOrganizationId?: string | null,
        careProviderPractitionerId?: string | null
    ) {
        const { optionalFollowUpConsultConditions: conditions } = await requestServiceConditionals(
            patientId,
            careProviderOrganizationId,
            careProviderPractitionerId
        );
        setOptionalFollowUpConsultConditions(conditions);
    }
}

export function tooV1ProcedureDiagnosticReportValidateOneCustomControlData(
    name: string,
    procedure: IProcedureAddEdit,
    errors: ValidationErrors
) {
    if (
        name === TooV1CustomControlFields.optometrische_diagnose ||
        tooV1RequiredFields.some((value) => name.includes(value))
    ) {
        errors = tooV1ProcedureDiagnosticReportValidateCustomControlData(procedure, errors);
    }

    return errors;
}

export function tooV1ProcedureDiagnosticReportValidateCustomControlData(
    procedure: IProcedureAddEdit,
    errors: ValidationErrors
) {
    const optometrische_diagnose: ITooDiagnosticReportCustomControlDataOptometrischeDiagnose =
        procedure.diagnosticReport?.customControlData.optometrische_diagnose;
    const procedureData: ITooProcedureCustomControlData = procedure.customControlData;

    const checkHasError = (hasError: boolean, key: string) =>
        hasError ? errors.set(key, 'any_required') : errors.remove(key);

    errors = checkHasError(
        Boolean(
            (optometrische_diagnose.glaucoom || procedureData.protocol === TooProtocol.glaucoom) &&
                !procedureData.pachymetrie_od
        ),
        'customControlData.pachymetrie_od'
    );
    errors = checkHasError(
        Boolean(
            (optometrische_diagnose.glaucoom || procedureData.protocol === TooProtocol.glaucoom) &&
                !procedureData.pachymetrie_os
        ),
        'customControlData.pachymetrie_os'
    );
    errors = checkHasError(
        Boolean(
            (optometrische_diagnose.glaucoom || procedureData.protocol === TooProtocol.glaucoom) &&
                !procedureData.gonioscopie_od
        ),
        'customControlData.gonioscopie_od'
    );
    errors = checkHasError(
        Boolean(
            (optometrische_diagnose.glaucoom || procedureData.protocol === TooProtocol.glaucoom) &&
                !procedureData.gonioscopie_os
        ),
        'customControlData.gonioscopie_os'
    );
    errors = checkHasError(
        Boolean(
            (optometrische_diagnose.macula_degeneratie || procedureData.protocol === TooProtocol.maculaDegeneratie) &&
                !procedureData.amsler_test_od
        ),
        'customControlData.amsler_test_od'
    );
    errors = checkHasError(
        Boolean(
            (optometrische_diagnose.macula_degeneratie || procedureData.protocol === TooProtocol.maculaDegeneratie) &&
                !procedureData.amsler_test_os
        ),
        'customControlData.amsler_test_os'
    );
    errors = checkHasError(
        Boolean(
            (optometrische_diagnose.cataract || procedureData.protocol === TooProtocol.cataract) &&
                !procedureData.nabije_visus_od
        ),
        'customControlData.nabije_visus_od'
    );
    errors = checkHasError(
        Boolean(
            (optometrische_diagnose.cataract || procedureData.protocol === TooProtocol.cataract) &&
                !procedureData.nabije_visus_os
        ),
        'customControlData.nabije_visus_os'
    );
    errors = checkHasError(
        Boolean(
            (optometrische_diagnose.cataract || procedureData.protocol === TooProtocol.cataract) &&
                procedureData.rijdt_auto === null
        ),
        'customControlData.rijdt_auto'
    );
    errors = checkHasError(
        Boolean(
            (optometrische_diagnose.cataract || procedureData.protocol === TooProtocol.cataract) &&
                procedureData.ervaart_klachten_als_storend === null
        ),
        'customControlData.ervaart_klachten_als_storend'
    );

    return errors;
}

function hasOptionalConsultDueToOptometricalDiagnosis(
    diagnosis: ITooDiagnosticReportCustomControlDataOptometrischeDiagnose,
    optionalFollowUpConsultConditions: OptionalFollowUpConsultCondition[]
): boolean {
    // Get all selected diagnosis entries
    const selectedDiagnoses = Object.entries(diagnosis)
        .filter(([, value]) => value)
        .map(([key]) => key as OptionalFollowUpConsultCondition);

    return (
        selectedDiagnoses.length > 0 && selectedDiagnoses.every((d) => optionalFollowUpConsultConditions.includes(d))
    );
}

function hasOptionalConsultDueToDiagnosis({
    conclusie_diagnose,
    optometrische_diagnose,
    other_diagnosis,
    optionalFollowUpConsultConditions,
}: {
    conclusie_diagnose: ConclusieDiagnoseOption | null;
    optionalFollowUpConsultConditions: OptionalFollowUpConsultCondition[];
    optometrische_diagnose: ITooDiagnosticReportCustomControlDataOptometrischeDiagnose;
    other_diagnosis: boolean;
}): boolean {
    if (other_diagnosis) {
        return false;
    }
    if (conclusie_diagnose && optionalFollowUpConsultConditions.includes(conclusie_diagnose)) {
        return true;
    }

    if (optometrische_diagnose) {
        return hasOptionalConsultDueToOptometricalDiagnosis(optometrische_diagnose, optionalFollowUpConsultConditions);
    }

    return false;
}

async function loadTocService(isTocConsumer: boolean): Promise<IService | undefined> {
    if (isTocConsumer) {
        const result = await fetchJson(`/api/services?code=${ServiceCode.TOC}`, 'GET');
        if (result.json.items) {
            return result.json.items[0];
        }
    }
}

async function isServiceConsumerOfToc(employmentId: string): Promise<boolean> {
    const url = `/api/serviceConsumers?employmentId=${employmentId}&isActive=true`;
    const result = await fetchJson(url, 'GET');

    return result.json.items.some((item: IServiceConsumer) => item.service.code === ServiceCode.TOC);
}

function checkRequiredFields(optometrische_diagnose: ITooDiagnosticReportCustomControlDataOptometrischeDiagnose) {
    const requiredFields: Partial<TooV1ProcedureRequiredFields> = {};

    if (optometrische_diagnose.glaucoom) {
        requiredFields.pachymetrie = true;
        requiredFields.gonioscopie = true;
    }

    if (optometrische_diagnose.macula_degeneratie) {
        requiredFields.amsler_test = true;
    }

    if (optometrische_diagnose.cataract) {
        requiredFields.nabije_visus = true;
        requiredFields.rijdt_auto = true;
        requiredFields.ervaart_klachten_als_storend = true;
    }

    return requiredFields;
}
